import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fmtCurrency } from "../lib/formatters";
import { WhyReserveCondensed } from "../components/WhyReserveModal";
import { FitOneLine } from "../components/FitOneLine";

// 2026-03 (Dave) — Context-sensitive Reserve Budget Report.
//
// Renders inside the SPA at hash route `#report/reserve-budget/{aid}`.
// The pill on the Association tab opens this in a new tab, so the
// component fetches its OWN association + calc + scenarios bundle
// (rather than relying on shared React state, which doesn't cross
// tabs).
//
// Design goals (Option A + Waterfall + Scenario count):
//   • Layout stays pixel-identical to the earlier static mockup
//     (`frontend/public/plan_report_owners.html`).
//   • Every number, year label, and count is live — see readmes above
//     each `{...}` binding.
//   • Prose stays generic so any association reads naturally without
//     naming specific inventory items.
//
// URL params supported via ?params=…:
//   plan_year_id      → optional plan year to render
//
// Data sources:
//   /api/associations/{aid}                → association + variables + elements
//   /api/associations/{aid}/calculate      → projection / funding level series
//   /api/associations/{aid}/scenarios      → count for "Scenarios Considered"

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Year marks the funding-level table displays, matching the Dashboard
// horizon selector granularity.
const YEAR_MARKS = [1, 5, 10, 15, 20, 25, 30];

// Funding-level band buckets — MUST match the Funding Level gauge
// on the Dashboard + the Funding Level Analysis chart:
//   • High Risk:    <  33.34 %
//   • Moderate Risk: 33.34 – 66.66 %
//   • Low Risk:     ≥ 66.67 %
// (2026-02-27, Dave) — previous version used 30/70 which flipped
// milestone bands vs the rest of the app for values in the 30-33
// and 67-70 windows.
function fundingBand(pct) {
  if (pct >= 66.67) return { label: "Low Risk", cls: "low" };
  if (pct >= 33.34) return { label: "Moderate Risk", cls: "mod" };
  return { label: "High Risk", cls: "high" };
}

// Compute the ending / full_funding_by_year[i] funding level for each
// requested year mark. Guards against short horizons by clamping.
// 2026-02-27 (Dave) — Also surface the raw Reserve Balance and Full
// Funding Balance so the milestones table can show the numerator /
// denominator behind the funding-level percentage (readers can then
// verify the ratio themselves).
function fundingLevelSeries(calc, marks) {
  const projection = calc.projection || [];
  const ffs = calc.full_funding_by_year || [];
  const years = calc.display?.years || [];
  return marks.map((mark) => {
    const idx = Math.min(mark - 1, projection.length - 1);
    const row = projection[idx];
    const pct = years[idx]?.funding_pct ?? 0; // server-computed
    return {
      mark,
      year: row.year,
      pct,
      band: fundingBand(pct),
      reserveBalance: row.ending,
      fullFunding: ffs[idx] || 0,
    };
  });
}

// Peak-expenditure row and low-balance row (server-computed years).
function peakAndLow(calc) {
  const projection = calc.projection || [];
  const S = calc.display?.horizons?.[String(projection.length)] || {};
  const peak = projection.find((r) => r.year === S.peak_expense_year) || projection[0];
  const low = projection.find((r) => r.year === S.low_balance_year) || projection[0];
  return { peak, low };
}

// {horizon}-year totals + averages for the Waterfall snapshot (server-computed).
function waterfallAverages(calc) {
  const S = calc.display?.horizons?.[String((calc.projection || []).length)] || {};
  return {
    beginning: S.beginning || 0,
    ending: S.ending || 0,
    avgContribution: S.avg_contributions || 0,
    avgInterest: S.avg_interest || 0,
    avgExpenditures: S.avg_expenditures || 0,
    avgSpecial: S.avg_special_assessments || 0,
    totalContribution: S.total_contributions || 0,
    totalInterest: S.total_interest || 0,
    totalExpenditures: S.total_expenditures || 0,
    totalSpecial: S.total_special_assessments || 0,
    specialsPerMember: S.specials_per_member || 0,
  };
}

// Compact currency: $1.34M / $177K / $35K etc. Used inside the
// waterfall bars where axis width is limited.
function fmtCompact(v) {
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
}

// 2026-03 (Dave) — Reserve Spending Mix aggregation for the compact
// donut on Page 2. Sums element-level amounts across every year of
// `expenditure_calendar`, then rolls anything under 5% of total spend
// into a single "Other" wedge (mirrors the on-screen chart's
// aggregation behaviour).
const MIX_SLICE_COLORS = ["#1C4E80", "#88BFDE", "#EA6A47", "#F4D35E", "#83C88F", "#7B6FCA", "#3A7FBD", "#D98E5A"];
const MIX_OTHER_COLOR = "#9AA9B4";

// Spending mix by category is server-computed (calc.display.spending_mix); colors are presentation.
function spendingMixSlices(mix) {
  const slices = (mix?.slices || []).map((r, i) => ({
    ...r,
    isOther: !!r.is_other,
    color: r.is_other ? MIX_OTHER_COLOR : MIX_SLICE_COLORS[i % MIX_SLICE_COLORS.length],
  }));
  return { slices, total: mix?.total || 0 };
}

// Compact SVG donut for the Reserve Spending Mix. Fits in ~150×150 so
// it can sit side-by-side with a legend column without adding a new
// page to the report.
function SpendingMixDonut({ slices, total }) {
  const cx = 75, cy = 75, r = 55, inner = 35;
  if (!slices.length) return null;
  let acc = 0;
  const arcs = slices.map((s) => {
    const start = (acc / total) * Math.PI * 2 - Math.PI / 2;
    const end = ((acc + s.amount) / total) * Math.PI * 2 - Math.PI / 2;
    acc += s.amount;
    const largeArc = end - start > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end),   y2 = cy + r * Math.sin(end);
    const xi2 = cx + inner * Math.cos(end),   yi2 = cy + inner * Math.sin(end);
    const xi1 = cx + inner * Math.cos(start), yi1 = cy + inner * Math.sin(start);
    const d = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${inner} ${inner} 0 ${largeArc} 0 ${xi1} ${yi1} Z`;
    return { d, color: s.color, key: s.name };
  });
  return (
    <svg viewBox="0 0 150 150" width="150" height="150" role="img" aria-label="Reserve Spending Mix donut chart">
      {arcs.map((a) => <path key={a.key} d={a.d} fill={a.color} />)}
      <text x={cx} y={cy - 4} textAnchor="middle" fontFamily="Source Sans 3" fontSize="9" fill="#5A6B75">Total</text>
      <text x={cx} y={cy + 10} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="12" fontWeight="700" fill="#1C4E80">{fmtCompact(total)}</text>
    </svg>
  );
}

// Live waterfall snapshot. 6 bars: Beginning · Contributions · Interest ·
// Expenses · Special Asmt · Ending. Top label shows the {horizon}-year
// TOTAL (matches the on-screen Reserve Summary chart on the Dashboard).
// Sub-line under each bar shows the per-year average.
//
// 2026-02-27 (Dave) — Rewrite v2 to mirror the Dashboard's
// `WaterfallChart` cumulative-range convention:
//   • Every bar sits ABOVE the axis line (no downward negatives).
//   • Bar heights represent the RANGE between running cumulative and
//     its neighbour, so Expenses reads as a step DOWN (top edge =
//     pre-expense cumulative, bottom edge = post-expense cumulative)
//     rather than a bar diving below zero.
//   • Palette matches the Dashboard: Beginning/Contributions #4A7BA5,
//     Interest #88BFDE, Expenses #0091D5 (lighter blue, per Dave's
//     2026-02-27 ask), Special Asmt #EA6A47, Ending #4A7BA5.
function WaterfallSnapshot({ wf, horizon }) {
  const beginning = wf.beginning;
  const contributions = wf.totalContribution;
  const interest = wf.totalInterest;
  const expenses = wf.totalExpenditures; // positive magnitude
  const specialAsmt = wf.totalSpecial;
  const ending = wf.ending;

  // Cumulative-range bars (same math as WaterfallChart.jsx):
  //   Beginning:     [0, beginning]              → total (grounded)
  //   Contributions: [c, c + contributions]      → additive step-up
  //   Interest:      [c, c + interest]           → additive step-up
  //   Expenses:      [c - expenses, c]           → step DOWN, above axis
  //   Special Asmt:  [c, c + specialAsmt]        → additive step-up
  //   Ending:        [0, ending]                 → total (grounded)
  let cursor = beginning;
  const rangeBars = [
    { key: "beg",  label: "Beginning",     sub: "Y1 START",                                 low: 0,                      high: beginning,               displayValue: beginning,      color: "#4A7BA5", isTotal: true,  sign: "pos" },
    { key: "con",  label: "Contributions", sub: fmtCurrency(Math.round(wf.avgContribution)), low: cursor,                 high: cursor + contributions,  displayValue: contributions,  color: "#4A7BA5", isTotal: false, sign: "pos" },
  ];
  cursor += contributions;
  rangeBars.push({ key: "int", label: "Interest", sub: fmtCurrency(Math.round(wf.avgInterest)), low: cursor, high: cursor + interest, displayValue: interest, color: "#88BFDE", isTotal: false, sign: "pos" });
  cursor += interest;
  rangeBars.push({ key: "exp", label: "Expenses", sub: fmtCurrency(Math.round(wf.avgExpenditures)), low: cursor - expenses, high: cursor, displayValue: expenses, color: "#0091D5", isTotal: false, sign: "neg" });
  cursor -= expenses;
  rangeBars.push({ key: "sa", label: "Special Asmt", sub: fmtCurrency(Math.round(wf.avgSpecial)), low: cursor, high: cursor + specialAsmt, displayValue: specialAsmt, color: "#EA6A47", isTotal: false, sign: "pos" });
  cursor += specialAsmt;
  rangeBars.push({ key: "end", label: "Ending", sub: `Y${horizon} END`, low: 0, high: ending, displayValue: ending, color: "#4A7BA5", isTotal: true, sign: "pos" });

  // ViewBox 700×230 kept identical to prior version so surrounding
  // panel layout / caption / spending-mix strip below don't shift.
  const axisY = 200;          // baseline (all bars sit above)
  const topPad = 30;          // headroom for amount labels above bars
  const plotH = axisY - topPad;
  const maxHigh = Math.max(1, ...rangeBars.map((b) => b.high));
  const yFor = (v) => axisY - (v / maxHigh) * plotH;

  return (
    <svg viewBox="0 0 700 230" width="100%" height="180" role="img" aria-label="Waterfall snapshot of horizon reserve activity">
      <line x1="20" y1={axisY} x2="680" y2={axisY} stroke="#E8ECF1" strokeWidth="1" />
      {rangeBars.map((b, i) => {
        const w = 72;
        const step = 110;
        const x = 46 + i * step;
        const yTop = yFor(b.high);
        const yBot = yFor(b.low);
        const h = Math.max(3, yBot - yTop);
        const labelY = yTop - 6;
        const prefix = b.isTotal ? "" : b.sign === "neg" ? "-" : "+";
        return (
          <g key={b.key}>
            <rect x={x} y={yTop} width={w} height={h} fill={b.color} />
            <text x={x + w / 2} y={labelY} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10" fontWeight="700" fill="#1A1A1A">
              {prefix}{fmtCompact(Math.abs(b.displayValue))}
            </text>
            <text x={x + w / 2} y={axisY + 15} textAnchor="middle" fontFamily="Source Sans 3" fontSize="9.5" fill="#5A6B75">{b.label}</text>
            <text x={x + w / 2} y={axisY + 26} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="8.5" fill="#5A6B75">{b.sub}</text>
          </g>
        );
      })}
    </svg>
  );
}

// Scenarios Considered table — same order as Scenario History's default (Target % asc, ties oldest first).
function ScenariosConsideredTable({ scenarios, units }) {
  const rows = scenarios.filter((s) => s.is_go_to || s.include_in_report).sort((a, b) =>
    (Number(a.target_pct) || 0) - (Number(b.target_pct) || 0)
    || new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
  if (!rows.length) return null;
  const firstYear = rows[0].first_year ?? "Year 1";
  const finalYear = rows[0].final_year ?? "Final Year";
  const cell = { padding: "5px 6px" };
  const num = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const th = { ...num, whiteSpace: "normal", lineHeight: 1.25, verticalAlign: "bottom", letterSpacing: ".06em" };
  const showPerMember = units > 0 && rows.some((s) => Number(s.special_assessments) > 0);
  return (
    <>
      <table className="u" style={{ marginTop: 6, fontSize: 10.5 }} data-testid="report-scenarios-table">
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>Scenario<br />Name</th>
            <th style={th}>Target<br />Funding %</th>
            <th style={th}>{firstYear}<br />Budget</th>
            <th style={th}>Special<br />Assessments</th>
            {showPerMember && <th style={th} data-testid="report-scenarios-sa-per-member-col">Special Assessments<br />Per Member</th>}
            <th style={th}>{finalYear}<br />Budget</th>
            <th style={th}>Funding %<br />Average</th>
            <th style={th}>Funding %<br />{finalYear}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.scenario_id} data-testid={`report-scenario-row-${s.scenario_id}`}>
              <td style={{ ...cell, wordBreak: "break-word" }}>
                {String(s.name || "").trim() || <span style={{ color: "var(--slate)", fontStyle: "italic" }}>Unnamed scenario</span>}
                {s.is_go_to && (
                  <span style={{ color: "#EA6A47", marginLeft: 4 }} title="Go-To scenario approved by the Board" data-testid="report-scenario-goto-star">&#9733;</span>
                )}
              </td>
              <td style={num}>{Math.round((Number(s.target_pct) || 0) * 100)}%</td>
              <td style={num}>{fmtCurrency(Math.round(s.year1_budget || 0))}</td>
              <td style={num}>{fmtCurrency(Math.round(s.special_assessments || 0))}</td>
              {showPerMember && <td style={num}>{fmtCurrency(Math.round((s.special_assessments || 0) / units))}</td>}
              <td style={num}>{fmtCurrency(Math.round(s.final_year_budget || 0))}</td>
              <td style={num}>{Math.round(Number(s.avg_funding_pct) || 0)}%</td>
              <td style={num}>{Math.round(Number(s.final_funding_pct) || 0)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.some((s) => s.is_go_to) && (
        <p style={{ fontSize: 10.5, color: "#5B6670", marginTop: 4 }}>
          <span style={{ color: "#EA6A47" }}>&#9733;</span> Go-To scenario approved by the Board.
        </p>
      )}
    </>
  );
}

export default function ReserveBudgetReport({ aid, onClose }) {
  const [assoc, setAssoc] = useState(null);
  const [calc, setCalc] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [err, setErr] = useState(null);
  // 2026-02-27 (Dave) — PDF download UI state. Kept here at the top
  // of the component so it stays above every conditional early
  // return (Rules of Hooks).
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  // Optional "Why of Reserve Funding" page after the cover — checked by default; `why=0` in the hash (PDF render) unticks it.
  const [includeWhy, setIncludeWhy] = useState(() => {
    try {
      const h = window.location.hash || "";
      const q = h.indexOf("?");
      return !(q >= 0 && new URLSearchParams(h.slice(q + 1)).get("why") === "0");
    } catch { return true; }
  });

  useEffect(() => {
    if (!aid) return;
    let cancelled = false;
    (async () => {
      try {
        // 2026-03 (Dave) — Honour the association's ACTIVE scenario if
        // the user has one loaded on the Association tab. Same
        // localStorage key App.js uses to persist the "goto" scenario.
        // Baseline (no key present, or key === baseline id) falls
        // through with no query param.
        //
        // 2026-02-27 (Dave) — Also honour a `scenario_id` supplied via
        // the URL hash query (`#report/reserve-budget/{aid}?scenario_id=…`).
        // The PDF endpoint uses this to render the same scenario the
        // caller was looking at when they clicked Download PDF, since
        // Playwright's browser context has no localStorage from the
        // real user's tab.
        let scenarioId = null;
        try {
          const hash = window.location.hash || "";
          const qIdx = hash.indexOf("?");
          if (qIdx >= 0) {
            const params = new URLSearchParams(hash.slice(qIdx + 1));
            scenarioId = params.get("scenario_id") || null;
          }
          if (!scenarioId) {
            scenarioId = window.localStorage.getItem(`ureserve.goto.${aid}`) || null;
          }
        } catch { /* ignore */ }
        // The report describes the Board-approved plan: the Go-To scenario wins when one exists.
        const sRes = await axios.get(`${API}/associations/${aid}/scenarios`);
        const goTo = (sRes.data.scenarios || []).find((s) => s.is_go_to);
        if (goTo) scenarioId = goTo.scenario_id;
        const qs = scenarioId ? `?scenario_id=${encodeURIComponent(scenarioId)}` : "";
        const [aRes, cRes] = await Promise.all([
          axios.get(`${API}/associations/${aid}${qs}`),
          axios.get(`${API}/associations/${aid}/calculate${qs}`),
        ]);
        if (cancelled) return;
        setAssoc(aRes.data);
        setCalc(cRes.data);
        setScenarios(sRes.data.scenarios || []);
        document.title = `uReserve · Reserve Budget Report — ${aRes.data.name}`;
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [aid]);

  const derived = useMemo(() => {
    if (!assoc || !calc) return null;
    const projection = calc.projection || [];
    const { peak, low } = peakAndLow(calc);
    const wf = waterfallAverages(calc);
    const fl = fundingLevelSeries(calc, YEAR_MARKS);
    const flNow = fl[0]?.pct || 0;
    const flEnd = fl[fl.length - 1]?.pct || 0;
    const bandNow = fundingBand(flNow);
    const bandEnd = fundingBand(flEnd);
    // 2026-03 (Dave) — Find the top elements driving the peak year so
    // the "Year XXXX — The Peak" callout can name them by name. Sourced
    // from `expenditure_calendar` (per-element amounts per year), which
    // the engine populates for exactly this kind of narrative use.
    const peakEntry = (calc.expenditure_calendar || []).find(
      (e) => e.year === peak.year,
    );
    const peakElements = ((peakEntry && peakEntry.elements) || [])
      .slice()
      .sort((a, b) => (b.amount || 0) - (a.amount || 0))
      .slice(0, 3);
    // Spending Mix slices across the full horizon.
    const mix = spendingMixSlices(calc.display?.spending_mix);
    return {
      projection, wf, fl, peak, low, flNow, flEnd, bandNow, bandEnd,
      peakElements, mix,
    };
  }, [assoc, calc]);

  // 2026-03-04 (Dave) — Own the preboot lifecycle on report routes.
  // AppGate deliberately does NOT remove `#u-preboot` when the hash
  // starts with `#report/reserve-budget/…` — the "Generating Reserve
  // Budget Report" spinner (see index.html) stays up until this
  // effect fires. Fires once `assoc && calc && derived` are all
  // ready, so the spinner hands off directly to a fully-rendered
  // report with no bare-text flash in between. Also fires on `err`
  // so a fetch failure surfaces the error banner instead of a
  // spinner-forever state.
  React.useEffect(() => {
    const ready = (assoc && calc && derived) || err;
    if (!ready) return;
    const el = document.getElementById("u-preboot");
    if (!el) return;
    el.classList.add("u-preboot--fading");
    const done = () => el.remove();
    el.addEventListener("transitionend", done, { once: true });
    setTimeout(done, 350); // reduced-motion / transition never fires
  }, [assoc, calc, derived, err]);

  if (err) {
    return (
      <div style={{ padding: 40, fontFamily: "system-ui", color: "#EA6A47" }}>
        <b>Could not load report:</b> {err}
      </div>
    );
  }
  if (!assoc || !calc || !derived) {
    // 2026-03-04 (Dave) — Render nothing while the preboot spinner
    // is still up (it covers the viewport at z-index 2147483000).
    // Previously showed "Loading Reserve Budget Report…" in the
    // top-left, which briefly poked through under the fading
    // preboot and read as a duplicate loading state.
    //
    // 2026-03-04 (Dave · Option A) — In MODAL mode there is no
    // preboot to hide behind (the app already booted long ago).
    // Render an in-modal "generating…" spinner that mirrors the
    // preboot's two-tone label so the user has feedback while the
    // three /api calls run.
    if (onClose) {
      // 2026-03-05 (Dave) — Match app-startup spinner exactly: single
      // row (spinner + text side-by-side), same 30 px navy title +
      // 26 px slate "loading" word from index.html's preboot styles.
      // We just borrow the same CSS classes rather than re-implement
      // the sizing, so the two loading states are visually identical.
      return (
        <div
          data-testid="report-modal-loading"
          className="u-preboot"
          style={{
            position: "absolute",
            inset: 0,
            background: "#fff",
            zIndex: "auto",
          }}
        >
          <div className="u-preboot__inner">
            <span className="u-preboot__spinner" aria-hidden="true" />
            <span className="u-preboot__label">
              <span className="u-preboot__label-loading">generating </span>
              <span className="u-preboot__label-brand">Reserve Budget Report</span>
            </span>
          </div>
        </div>
      );
    }
    return null;
  }

  const { projection, wf, fl, peak, low, flNow, flEnd, bandNow, bandEnd, peakElements, mix } = derived;

  const studyYear = assoc.study_year;
  const horizon = projection.length;
  const endYear = projection[horizon - 1]?.year || studyYear + horizon - 1;
  const members = assoc.unit_count || calc.members || 0;
  const pmpm = calc.per_member_per_month || 0;
  const openingBalance = projection[0]?.beginning || assoc.opening_balance || 0;
  const inflationRate = assoc.variables?.inflation_rate || 0;
  const interestRate = assoc.variables?.interest_rate || 0;
  const targetFundingPct = (assoc.variables?.target_pct || 0) * 100;
  // 2026-03 (Dave) — Year 1 adjustment + phase-out. Adjustment is
  // stored as a signed fraction (e.g. -0.30 = 30% Y1 discount; +0.15
  // = 15% Y1 uplift). Adjustment period is how many years the ramp
  // takes to reach full contribution (0 = no ramp, 5 = phased over
  // the first 5 years, etc.). Both feed the "Years 1-5 · The Runway"
  // paragraph on Page 3.
  const adjustmentPct = (assoc.variables?.adjustment_pct || 0) * 100;
  const adjustmentPeriod = assoc.variables?.adjustment_period || 0;
  const priorAnnual = assoc.prior_year_contribution || 0;
  // Prior-year PMPM + Year-1 PMPM change (%) are server-computed (calc.display).
  const priorPMPM = calc.display?.pmpm_prior || 0;
  const pmpmDeltaPct = calc.display?.pmpm_delta_pct ?? null;
  const scenarioCount = scenarios.length;
  const draftDate = new Date().toISOString().slice(0, 10);
  const metaLine = `DRAFT · ${draftDate} · ${assoc.name}`;
  const footerLine = `Reserve Budget Report · ${assoc.name} · Plan Year ${studyYear}`;

  // 2026-02-27 (Dave) — "Download PDF" pill in the sticky banner.
  // Hits the backend `GET /associations/{aid}/report.pdf` endpoint,
  // which drives a headless Chromium via Playwright to render THIS
  // exact page and stream a Letter-sized PDF back. One click →
  // browser saves the file. Falls back to a friendly error toast if
  // the server call fails (network, auth, timeout, etc.).
  const handleDownloadPdf = async () => {
    if (pdfDownloading) return;
    setPdfError(null);
    setPdfDownloading(true);
    // 2026-02-27 (evening, Dave) — Show a persistent "Preparing your
    // report…" toast while the backend drives Chromium to render the
    // PDF (typically 8-12 s, up to ~30 s under load). Dismissed in
    // finally{} so both success and error paths clear it.
    const toastId = toast.loading("Preparing your report… (~10 s)", {
      description: "Rendering the PDF — hang tight.",
      duration: Infinity,
    });
    try {
      // Pass the active scenario id through so the PDF matches what
      // the caller sees on screen.
      let scenarioId = null;
      try {
        scenarioId = window.localStorage.getItem(`ureserve.goto.${aid}`) || null;
      } catch { /* ignore */ }
      const params = new URLSearchParams();
      if (scenarioId) params.set("scenario_id", scenarioId);
      params.set("why", includeWhy ? "1" : "0");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/associations/${aid}/report.pdf${qs}`, {
        responseType: "blob",
        // The backend can spend 5-10 s driving Chromium; give it room.
        timeout: 120000,
      });
      // Filename from Content-Disposition (RFC 5987 `filename*=UTF-8''…`
      // preferred; ASCII `filename="…"` fallback).
      const disp = res.headers["content-disposition"] || "";
      let filename = `uReserve — ${assoc?.name || "Report"} — Reserve Budget ${studyYear}.pdf`;
      const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disp);
      const ascii = /filename="([^"]+)"/i.exec(disp);
      if (utf8) filename = decodeURIComponent(utf8[1]);
      else if (ascii) filename = ascii[1];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Report downloaded", { id: toastId, duration: 2500 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Download failed.";
      setPdfError(msg);
      toast.error("Report download failed", {
        id: toastId,
        description: msg,
        duration: 6000,
      });
      // Auto-clear the inline banner after 6 seconds.
      setTimeout(() => setPdfError(null), 6000);
    } finally {
      setPdfDownloading(false);
    }
  };

  const whyOffset = includeWhy ? 1 : 0;
  const totalPages = 6 + whyOffset;

  return (
    <div className="reserve-budget-report-root">
      <style>{styles}</style>

      <div className="banner">
        <div>uReserve · Reserve Budget Report</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }} data-testid="include-why-label">
            <input
              type="checkbox"
              checked={includeWhy}
              onChange={(e) => setIncludeWhy(e.target.checked)}
              data-testid="include-why-checkbox"
            />
            Include Why Reserve page
          </label>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={pdfDownloading}
            data-testid="download-pdf-btn"
            className="dl-pdf-btn"
            title="Download this report as a PDF"
            aria-busy={pdfDownloading}
          >
            {pdfDownloading ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6 }}>
                <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                <path d="M21 12a9 9 0 0 0-9-9">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                </path>
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6 }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            {pdfDownloading ? "Building PDF…" : "Download PDF"}
          </button>
          {onClose && (
            // 2026-03-04 (Dave · Option A) — Modal-mode close X. Only
            // rendered when `onClose` is supplied (i.e., the report
            // was opened as an in-app overlay); the standalone-tab
            // render path passes no `onClose` so the X stays hidden
            // and the print/PDF layout is unaffected.
            <button
              type="button"
              onClick={onClose}
              data-testid="close-report-modal"
              title="Close report (Esc)"
              aria-label="Close report"
              className="dl-pdf-btn"
              style={{ padding: "0 8px", minWidth: 28, justifyContent: "center" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {pdfError && (
        <div
          className="pdf-error-toast"
          role="alert"
          data-testid="download-pdf-error"
        >
          Couldn&rsquo;t build the PDF: {pdfError}
        </div>
      )}

      <div className="stack">

        {/* ═════ Page 1 · Branded cover ═════ */}
        <section className="page" id="p1">
          <div className="page-inner" style={{ display: "flex", flexDirection: "column", height: "calc(11in - 1.65in)" }}>
            <div className="ph" style={{ justifyContent: "flex-end" }}>
              <div className="meta">{metaLine}</div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 .1in" }}>
              <div style={{ transform: "scale(0.67)", transformOrigin: "top center", margin: "0 auto" }}>
                <img src="/ureserve-logo.png" alt="uReserve Planning" style={{ maxHeight: 98, width: "auto", display: "block", margin: "0 auto 20px" }} />
                <div style={{ textAlign: "center" }}>
                  {/* 2026-03-05 (Dave) — Badge border/background dropped;
                      label promoted to a larger, centered heading with
                      generous top spacing so it reads as the primary
                      subtitle beneath the logo. */}
                  <div style={{ marginTop: 44, fontSize: 32, letterSpacing: ".14em", color: "var(--navy)", fontWeight: 700, textTransform: "uppercase" }}>
                    Reserve Budget Report
                  </div>
                  <div style={{ marginTop: 28, fontSize: 42, color: "var(--navy)", fontWeight: 700, lineHeight: 1.15 }}>{assoc.name}</div>
                  {/* 2026-03-05 (Dave) — Plan Year bolded (was 700 already;
                      raised weight visibility by dropping the italic-ish
                      wide letter-spacing very slightly and keeping bold). */}
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 30, color: "var(--slate)", letterSpacing: ".14em", marginTop: 10, fontWeight: 800 }}>
                    PLAN YEAR {studyYear}
                  </div>
                  {(() => {
                    const g = (scenarios || []).find((s) => s.is_go_to);
                    if (!g || !String(g.name || "").trim()) return null;
                    return (
                      <div
                        data-testid="report-cover-goto-badge"
                        style={{
                          display: "inline-flex", flexDirection: "column", alignItems: "center", marginTop: 26,
                          padding: "12px 34px", border: "2px solid var(--sky)", borderRadius: 8,
                          background: "#F2F9FD", color: "var(--navy)", fontSize: 36, fontWeight: 700,
                          lineHeight: 1.15, textAlign: "center", maxWidth: "100%", boxSizing: "border-box",
                        }}
                      >
                        <FitOneLine text={g.name} max={36} min={14} />
                      </div>
                    );
                  })()}
                  {/* 2026-03-05 (Dave) — Coral border + peach fill removed;
                      the "Prepared for Members" block now reads as clean
                      typography stacked below the association meta. */}
                  <div style={{ marginTop: 56, textAlign: "center" }}>
                    <div style={{ fontSize: 34, color: "var(--coral)", fontWeight: 900, letterSpacing: ".12em" }} data-testid="report-cover-draft">- DRAFT -</div>
                    <div style={{ fontSize: 22, color: "var(--slate)", fontWeight: 700, marginTop: 8 }} data-testid="report-cover-digest">
                      A plain-language digest of the Board&rsquo;s reserve budget.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pf"><div>{footerLine}</div><div>Page 1 of {totalPages}</div></div>
        </section>

        {includeWhy && (
          <section className="page" id="p-why" data-testid="report-why-page">
            <div className="page-inner" style={{ display: "flex", flexDirection: "column", height: "calc(11in - 1.65in)" }}>
              <div className="ph" style={{ justifyContent: "flex-end" }}>
                <div className="meta">{metaLine}</div>
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <WhyReserveCondensed showStatutory />
              </div>
            </div>
            <div className="pf"><div>{footerLine}</div><div>Page 2 of {totalPages}</div></div>
          </section>
        )}

        {/* ═════ Page 2 · Projection + Numbers + Funding Level ═════ */}
        <section className="page" id="p2">
          <div className="page-inner">
            <div className="ph">
              <div className="brand"><span className="u">u</span><span className="r"><em>Reserve</em> Planning, LLC</span></div>
              <div className="meta">{metaLine}</div>
            </div>

            {/* 2026-02-27 (late-evening, Dave) — Forward moved to
                the TOP of Page 2 (was: after the waterfall). Reads
                as a lede that sets the reader's expectations before
                any numbers appear. `mt-forward` locks a small top
                margin so it sits right under the coral header rule. */}
            <h2 className="section" style={{ marginTop: 2 }}>Foreword</h2>
            <div className="section-rule" />
            <p style={{ marginBottom: 10 }}>
              This digest is written for members of the board, owner-members, and advisers. It explains, in plain English,
              what your association is saving for, how much it costs each month,
              and where the reserve stands at key mileposts across the {horizon}-year plan.
            </p>
            {/* 2026-03-05 (Dave) — Watermark retired from all four
                pages; the disclaimer it carried now lives here as a
                proper Forward paragraph so it's part of the reading
                flow instead of a rotated background stamp. */}
            <p style={{ marginBottom: 10 }}>
              This document is an internal budget plan and does not
              constitute a formal accredited Reserve Study.
            </p>

            {/* 2026-03-06 (Dave) — "Numbers at a Glance" promoted to
                sit directly under the Forward. Rationale: readers see
                the six headline KPIs (Opening Balance → Elements →
                Your Contribution → Funding Level → Peak → Special)
                before wading into assumptions and the waterfall.
                Previously lived below the Contributions-by-Year
                strip; moved up per Dave. */}
            <h2 className="section" style={{ marginTop: 32 }}>The Numbers at a Glance</h2>
            <div className="section-rule" />
            <div className="kpi-strip">
              <div className="kpi"><div className="h">Opening Balance</div><div className="v">{fmtCompact(openingBalance)}</div><div className="sub">start of {studyYear}</div></div>
              {/* 2026-03-06 (Dave) — Elements KPI. Count = elements
                  included in the plan; Current Cost = sum of
                  `extended_cost` (fall back to `current_cost`) across
                  those same elements at study-year dollars. Sits 2nd
                  from the left so the strip reads: balance → what you
                  own → what you pay → funding health → peak → special.
                  Compact currency (K/M) keeps the value on one line
                  even in a 6-column strip. */}
              {(() => {
                const included = (assoc.elements || []).filter((el) => el.included_in_plan !== false);
                const currentCost = assoc.element_totals?.total_all ?? 0; // server-computed
                return (
                  <div className="kpi" data-testid="kpi-elements">
                    <div className="h">Elements ({included.length})</div>
                    <div className="v">{fmtCompact(currentCost)}</div>
                    <div className="sub">current cost</div>
                  </div>
                );
              })()}
              {/* 2026-03-06 (Dave) — Year-1 Contribution KPI. Total
                  dollars flowing INTO the reserve in Plan Year 1
                  (annualised). Sits 3rd from the left so members see
                  the total plan-wide inflow adjacent to their own
                  per-month share ("Your Contribution", 4th). Uses
                  compact currency (K/M) to stay on one line inside
                  the 7-column strip. */}
              <div className="kpi" data-testid="kpi-year1-contribution">
                <div className="h">Year {studyYear} Contribution</div>
                <div className="v">{(() => {
                  const v = Math.round(projection[0]?.contribution || 0);
                  return Math.abs(v) >= 1_000 && Math.abs(v) < 1_000_000 ? `$${(v / 1_000).toFixed(1)}K` : fmtCompact(v);
                })()}</div>
                <div className="sub">plan-wide, annual</div>
              </div>
              <div className="kpi">
                <div className="h">Per Member Contribution</div>
                <div className="v">{fmtCurrency(pmpm, 2)}/mo</div>
                <div className="sub">per member ({members.toLocaleString()})</div>
                {/* 2026-03 (Dave) — Year-1 PMPM vs prior year. Coral
                    arrow up = increase, green arrow down = decrease,
                    slate for flat. Suppressed entirely if prior-year
                    contribution is unknown / zero. */}
                {pmpmDeltaPct !== null && (
                  <div
                    className="sub"
                    style={{
                      color:
                        pmpmDeltaPct > 0.05 ? "var(--coral)"
                        : pmpmDeltaPct < -0.05 ? "#0F7A55"
                        : "var(--slate)",
                      fontWeight: 700,
                      marginTop: 1,
                    }}
                  >
                    {pmpmDeltaPct > 0.05 ? "\u2191 " : pmpmDeltaPct < -0.05 ? "\u2193 " : ""}
                    {Math.abs(pmpmDeltaPct) < 0.05 ? "flat" : `${Math.abs(pmpmDeltaPct).toFixed(1)}%`}
                    {" vs prior year"}
                  </div>
                )}
              </div>
              {/* 2026-02-27 (Dave) — Funding Level KPI now reflects the
                  end-of-plan (Year {horizon}) funding level to match
                  the Dashboard's risk-band marker (which is driven by
                  the horizon selector at the top-right and defaults to
                  Year 30). Previously showed flNow (Year 1) which
                  disagreed with the Dashboard for every horizon > 1.
                  The full year-by-year progression is still visible in
                  the "Funding Level at Key Mileposts" table below. */}
              <div className="kpi" data-testid="kpi-funding-level"><div className="h">Funding Level</div><div className="v">{flEnd.toFixed(0)}%</div><div className="sub">{bandEnd.label.toLowerCase()} @ {endYear}</div></div>
              <div className="kpi"><div className="h">Peak Year Expenditure</div><div className="v coral">{fmtCompact(peak.expenditures)}</div><div className="sub">{peak.year}</div></div>
              {/* 2026-03 (Dave) — Special Assessments card. TOTAL over
                  the {horizon}-year plan on top; per-member total on
                  the sub-line so members see both the plan-wide charge
                  and their individual share. */}
              <div className="kpi">
                <div className="h">Special Assessments</div>
                <div className="v coral">{fmtCompact(wf.totalSpecial)}</div>
                <div className="sub">
                  {members > 0 && wf.totalSpecial > 0
                    ? `${fmtCurrency(Math.round(wf.specialsPerMember))} per member`
                    : members > 0 && wf.totalSpecial === 0
                      ? "none planned"
                      : "\u00A0"}
                </div>
              </div>
            </div>

            {/* 2026-02-27 (Dave) — Key assumptions strip. Single compact
                row (four items, ~16 px tall) so it fits on Page 2
                without pushing content to a new page. Values pulled
                from the ACTIVE scenario's variables so the assumptions
                the reader sees always match the numbers driving the
                waterfall + KPIs below. Year-1 Adjustment is signed
                (negative = discount, positive = uplift). */}
            <div className="assumptions-strip" data-testid="report-assumptions-strip">
              <span className="a-label">Key Assumptions</span>
              <span className="a-item"><span className="a-k">Target Funding Level</span><span className="a-v" data-testid="assump-target">{targetFundingPct.toFixed(0)}%</span></span>
              <span className="a-sep">·</span>
              <span className="a-item"><span className="a-k a-k-dark">Inflation Rate</span><span className="a-v" data-testid="assump-inflation">{(inflationRate * 100).toFixed(1)}%</span></span>
              <span className="a-sep">·</span>
              <span className="a-item"><span className="a-k a-k-dark">Interest Earned</span><span className="a-v" data-testid="assump-interest">{(interestRate * 100).toFixed(2)}%</span></span>
              <span className="a-sep">·</span>
              <span className="a-item"><span className="a-k">Year 1 Adjustment</span><span className="a-v" data-testid="assump-adjustment">{adjustmentPct > 0 ? "+" : ""}{adjustmentPct.toFixed(0)}%</span></span>
            </div>

            <h2 className="section" style={{ marginTop: 32 }}>Reserve Projection Summary</h2>
            <div className="section-rule" />
            <div className="waterfall-wrap">
              <WaterfallSnapshot wf={wf} horizon={horizon} />
              <div className="cap">
                Averaged annual reserve activity across the {horizon}-year plan
                (Plan Year {studyYear} &ndash; {endYear}).
              </div>
              {/* 2026-03 (Dave) — Reserve Spending Mix — compact
                  side-by-side donut + legend. Kept inside the same
                  panel card so the report length doesn't grow. */}
              {mix.slices.length > 0 && (
                <div className="mix-strip">
                  <SpendingMixDonut slices={mix.slices} total={mix.total} />
                  <div className="mix-legend">
                    <div className="mix-title">Reserve Spending Mix</div>
                    {mix.slices.map((s) => (
                      <div className="mix-row" key={s.name}>
                        <span className="dot" style={{ background: s.color }} />
                        <span className="lbl">{s.name}</span>
                        <span className="pct">{s.pct.toFixed(1)}%</span>
                        <span className="amt">{fmtCompact(s.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
          <div className="pf"><div>{footerLine}</div><div>Page {2 + whyOffset} of {totalPages}</div></div>
        </section>

        {/* ═════ Page 3 · Contributions + Funding Level Milestones ═════
            2026-03-06 (Dave) — Split off from Page 2. Page 2 grew too
            tall after promoting the KPI strip up under the Forward,
            and the prior workaround (Chromium zoom:0.78 in the PDF
            renderer) squeezed Page 2 to ~78% width in the printed
            PDF. Cleaner fix: split into two natural pages so every
            physical page prints edge-to-edge at 100%. */}
        <section className="page" id="p2b">
          <div className="page-inner">
            <div className="ph">
              <div className="brand"><span className="u">u</span><span className="r"><em>Reserve</em> Planning, LLC</span></div>
              <div className="meta">{metaLine}</div>
            </div>

            {/* 2026-02-27 (late-evening, Dave) — Contributions by Year
                strip. Single compact row showing the first 5 plan years'
                reserve contributions (year → dollar amount) so members
                see the near-term ramp at a glance. Rendered right below
                the waterfall so the projection story reads
                averages → per-year detail. Uses the same visual DNA as
                the Key Assumptions strip (navy label, mono values,
                coral-adjacent Sky left-border) to feel like a
                sibling data strip and avoid vertical inflation. */}
            <h2 className="section" style={{ marginTop: 20 }}>Annual Contributions</h2>
            <div className="section-rule" />
            <div className="assumptions-strip contrib-grid" data-testid="report-contrib-first-5" style={{ marginTop: 8 }}>
              {priorAnnual > 0 && (
                <>
                  <span className="a-item" data-testid="report-contrib-prior-year">
                    <span className="a-k">Prior Year <span style={{ fontWeight: 400 }}>({studyYear - 1})</span></span>
                    <span className="a-v">{fmtCurrency(Math.round(priorAnnual))}</span>
                  </span>
                  <span className="a-sep">·</span>
                </>
              )}
              {projection.slice(0, 5).map((r, i, arr) => (
                <React.Fragment key={r.year}>
                  <span className="a-item" data-testid={`report-contrib-year-${i + 1}`}>
                    <span className="a-k">Year {i + 1} <span style={{ fontWeight: 400 }}>({r.year})</span></span>
                    <span className="a-v">{fmtCurrency(Math.round(r.contribution || 0))}</span>
                  </span>
                  {i < arr.length - 1 && <span className="a-sep">·</span>}
                </React.Fragment>
              ))}
            </div>

            <h2 className="section" style={{ marginTop: 64 }}>Scenarios Considered</h2>
            <div className="section-rule" />
            <p>
              Before settling on this plan, the Board evaluated{" "}
              <b data-testid="report-scenario-count">{scenarioCount}</b>{" "}
              {scenarioCount === 1 ? "scenario" : "scenarios"}, taking into account
              the presumed risk profile of our community. Each scenario
              tested a different assumption &mdash; contribution timing,
              inflation, target funding level, or expenditure timing &mdash;
              so the chosen plan is the one that best fits the association&rsquo;s
              actual position and risk tolerance.
            </p>
            <ScenariosConsideredTable scenarios={scenarios} units={members} />

            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 64 }}>
              <h2 className="section" style={{ marginTop: 0 }}>Funding Level at Key Mileposts</h2>
              <div style={{ display: "flex", gap: 12, fontSize: 9.5, color: "var(--ink)" }} data-testid="report-funding-band-legend">
                {[["High Risk", "0–33%", "#FBDDD2", "#EA6A47"], ["Moderate Risk", "34–66%", "#FCF1D3", "#B78900"], ["Low Risk", "67%+", "#E4F5EA", "#0F7A55"]].map(([label, range, bg, fg]) => (
                  <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: bg, border: `1px solid ${fg}`, display: "inline-block" }} />
                    <b style={{ color: fg, fontWeight: 700 }}>{label}</b>
                    <span style={{ color: "var(--slate)" }}>{range}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="section-rule" />
            <p style={{ fontSize: 9.5, color: "var(--slate)", margin: "4px 0 6px" }} data-testid="report-funding-band-explainer">
              <b style={{ color: "#EA6A47" }}>High Risk</b> means a major repair could force a special assessment or loan;{" "}
              <b style={{ color: "#B78900" }}>Moderate Risk</b> means most repairs are covered, but a large one may need extra funds;{" "}
              <b style={{ color: "#0F7A55" }}>Low Risk</b> means savings are on track to pay for repairs as they come due.
            </p>
            <table className="fl" data-testid="funding-level-milestones">
              <thead>
                <tr>
                  <th>Year Mark</th>
                  <th>Plan Year</th>
                  <th>Reserve Balance</th>
                  <th>Full Funding Balance</th>
                  <th>Funding %<br />at Period End</th>
                  <th>Funding %<br />{projection[projection.length - 1]?.year}</th>
                  <th>Risk Band</th>
                </tr>
              </thead>
              <tbody>
                {fl.filter((r) => [10, 20, 30].includes(r.mark)).map((r) => (
                  <tr key={r.mark}>
                    <td className="yr">Year {r.mark}</td>
                    <td className="yr">{r.year}</td>
                    <td className="yr">{fmtCurrency(Math.round(r.reserveBalance || 0))}</td>
                    <td className="yr">{fmtCurrency(Math.round(r.fullFunding || 0))}</td>
                    <td className={`pct band-${r.band.cls}`} data-testid={`milepost-period-pct-${r.mark}`}>{r.pct.toFixed(0)}% <span style={{ fontWeight: 400, fontSize: "0.8em", color: "var(--slate)" }}>({r.year})</span></td>
                    <td className="pct" data-testid={`milepost-final-pct-${r.mark}`}>{(fl[fl.length - 1]?.pct || 0).toFixed(0)}%</td>
                    <td className={`band-${r.band.cls}`}>{r.band.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 10.5, color: "var(--slate)", marginTop: 6, fontStyle: "italic" }}>
              Funding Level answers: <i>if we had to pay our fair share of every
              future repair today, do we have the cash?</i> The Board&rsquo;s
              target for this plan is <b>{targetFundingPct.toFixed(0)}%</b>.
            </p>
          </div>
          <div className="pf"><div>{footerLine}</div><div>Page {3 + whyOffset} of {totalPages}</div></div>
        </section>

        {/* ═════ Page 4 · The Plan in Plain English ═════ */}
        <section className="page" id="p3">
          <div className="page-inner">
            <div className="ph">
              <div className="brand"><span className="u">u</span><span className="r"><em>Reserve</em> Planning, LLC</span></div>
              <div className="meta">{metaLine}</div>
            </div>

            <h2 className="section">The Plan in Plain English</h2>
            <div className="section-rule" />
            <p className="lede">
              Here is what happens over the next {horizon} years if the plan runs
              as expected.
            </p>

            {(() => {
              const absAdj = Math.abs(adjustmentPct).toFixed(0);
              const rampYears = adjustmentPeriod || 0;
              const hasAdjustment = adjustmentPct !== 0 && rampYears > 0;
              // Runway = the phase-out period (or a nominal 5-year window
              // if no adjustment is defined). Buffer + Recovery headings
              // pick up where the runway ends.
              const runwayEnd = hasAdjustment ? rampYears : 5;
              const bufferStart = runwayEnd + 1;
              return (
                <>
                  <h3 className="h3">{runwayEnd === 1 ? "Year 1" : <>Years 1&ndash;{runwayEnd}</>} &mdash; The Runway</h3>
                  {!hasAdjustment && (
                    <p>
                      Contribution opens at <b>{fmtCurrency(pmpm, 2)} per member per month</b>
                      {" "}with no Year-1 adjustment applied &mdash; members pay the
                      full inflation-indexed contribution from day one. Early
                      expenditures are modest and are absorbed by the reserve
                      without stress.
                    </p>
                  )}
                  {hasAdjustment && adjustmentPct < 0 && (
                    <p>
                      Contribution opens at <b>{fmtCurrency(pmpm, 2)} per member per month</b>,
                      reflecting a <b>{absAdj}% Year-1 discount</b> against the
                      un-adjusted target.{" "}
                      {rampYears === 1 ? (
                        <>
                          That discount <b>applies in Plan Year {studyYear} only</b>; the
                          contribution returns to its full inflation-indexed level in
                          Plan Year <b>{studyYear + 1}</b>.
                        </>
                      ) : (
                        <>
                          That discount is <b>phased out linearly
                          over {rampYears} years</b> so the
                          contribution rises in even steps and reaches its full
                          inflation-indexed level in Plan Year{" "}
                          <b>{studyYear + rampYears}</b>. The gradual ramp lets
                          members plan for the increase instead of absorbing a
                          step-change in Year 2.
                        </>
                      )}{" "}
                      Early expenditures during the
                      runway are modest and are absorbed without stress.
                    </p>
                  )}
                  {hasAdjustment && adjustmentPct > 0 && (
                    <p>
                      Contribution opens at <b>{fmtCurrency(pmpm, 2)} per member per month</b>,
                      reflecting a <b>{absAdj}% Year-1 uplift</b> above the
                      un-adjusted target &mdash; a deliberate front-loaded
                      adjustment.{" "}
                      {rampYears === 1 ? (
                        <>
                          That uplift <b>applies in Plan Year {studyYear} only</b>; the
                          contribution settles at its inflation-indexed baseline in
                          Plan Year <b>{studyYear + 1}</b>.
                        </>
                      ) : (
                        <>
                          That uplift is <b>phased out linearly over
                          {" "}{rampYears} years</b>, so the
                          contribution trends downward in even steps and settles at
                          its inflation-indexed baseline in Plan Year{" "}
                          <b>{studyYear + rampYears}</b>.
                        </>
                      )}{" "}
                      Front-loading builds the
                      reserve faster while early expenditures remain modest.
                    </p>
                  )}

                  <h3 className="h3">Years {bufferStart}&ndash;15 &mdash; Building the Buffer</h3>
                  <p>
                    Contribution reaches its full inflation-indexed level
                    (indexed at <b>{(inflationRate * 100).toFixed(1)}%</b> per
                    year in this plan). Mid-plan expenditures draw down the
                    reserve in controlled fashion while the buffer builds in
                    preparation for the plan&rsquo;s peak.
                  </p>
                </>
              );
            })()}

            <h3 className="h3">Year {peak.year} &mdash; The Peak</h3>
            <div className="callout">
              <div className="label">Peak Year {peak.year} · Expenditure {fmtCurrency(Math.round(peak.expenditures))}</div>
              {peakElements.length > 0 && (
                <>
                  {peakElements.length === 1 && (
                    <><b>{peakElements[0].name}</b> lands in {peak.year}, producing</>
                  )}
                  {peakElements.length === 2 && (
                    <><b>{peakElements[0].name}</b> and <b>{peakElements[1].name}</b> land in the same year, producing</>
                  )}
                  {peakElements.length >= 3 && (
                    <>
                      <b>{peakElements[0].name}</b>, <b>{peakElements[1].name}</b>, and{" "}
                      <b>{peakElements[2].name}</b> land in the same year, producing
                    </>
                  )}
                  {" "}the plan&rsquo;s single-year peak expenditure of{" "}
                  <b>{fmtCurrency(Math.round(peak.expenditures))}</b>.{" "}
                </>
              )}
              {peakElements.length === 0 && (
                <>
                  This is the plan&rsquo;s single-year peak expenditure of{" "}
                  <b>{fmtCurrency(Math.round(peak.expenditures))}</b>.{" "}
                </>
              )}
              The reserve draws from <b>{fmtCurrency(Math.round(peak.beginning))}</b> down to
              {" "}<b>{fmtCurrency(Math.round(peak.ending))}</b>. This is the
              year the plan is built to absorb.
            </div>

            <h3 className="h3">Years 16&ndash;{horizon} &mdash; Recovery</h3>
            <p>
              Post-peak, expenditures return to a normal cadence. The reserve
              rebuilds to <b>{fmtCurrency(Math.round(projection[horizon - 1].ending))}</b> by
              the end of the plan, with funding level climbing from{" "}
              <b>{flNow.toFixed(0)}%</b> to <b>{flEnd.toFixed(0)}%</b> ({bandEnd.label.toLowerCase()} band).
            </p>

            <p style={{ marginTop: 14 }}>
              <b>Bottom line:</b> the plan pays for itself out of monthly
              contributions across a {horizon}-year horizon. The
              {/* 2026-02-27 (Dave) — lead with the year and mirror
                  the "Low Balance Year: YYYY ($ZZZ)" phrasing used
                  by the Projections pill so both surfaces read the
                  same way. */}
              {" "}<b>Low Balance Year is {low.year}</b>, when the reserve
              dips to <b>{fmtCurrency(Math.round(low.ending))}</b>.
            </p>
          </div>
          <div className="pf"><div>{footerLine}</div><div>Page {4 + whyOffset} of {totalPages}</div></div>
        </section>

        {/* ═════ Page 4 · Trade-offs + Scenarios + Triggers ═════ */}
        <section className="page" id="p4">
          <div className="page-inner">
            <div className="ph">
              <div className="brand"><span className="u">u</span><span className="r"><em>Reserve</em> Planning, LLC</span></div>
              <div className="meta">{metaLine}</div>
            </div>

            <h2 className="section">The Choice Behind the Plan</h2>
            <div className="section-rule" />
            <p className="lede">
              Every reserve decision is a trade-off between what members pay
              today and what the community risks paying later. The Board
              chose &ldquo;steady now&rdquo; over &ldquo;shock later.&rdquo;
            </p>

            <table className="u">
              <thead><tr><th>&nbsp;</th><th>Road A &mdash; Fund Steadily <b>(chosen)</b></th><th>Road B &mdash; Under-Fund Now</th></tr></thead>
              <tbody>
                <tr>
                  <td><b>What you feel today</b></td>
                  <td>Predictable monthly contribution.</td>
                  <td>Lower monthly contribution.</td>
                </tr>
                <tr>
                  <td><b>At Peak Year {peak.year} ({fmtCompact(peak.expenditures)})</b></td>
                  <td>Reserve absorbs the draw. No assessment.</td>
                  <td style={{ color: "var(--coral)" }}><b>Reserve short. Special assessment likely.</b></td>
                </tr>
                <tr>
                  <td><b>Total {horizon}-year cost</b></td>
                  <td>Lower &mdash; costs spread and inflation-managed.</td>
                  <td>Higher &mdash; deferred repairs cost more.</td>
                </tr>
              </tbody>
            </table>

            <div className="callout navy" style={{ marginTop: 14 }}>
              <div className="label">Trade-off in One Sentence</div>
              <b>Pay a little more each month for {horizon} years, or risk paying
              a lot more at once, at the worst possible time.</b>
            </div>

            <h3 className="h3">What Should Trigger a Fresh Look?</h3>
            <p>
              This plan will be re-run if any one or more of the following
              assumptions change in a material way: projected inflation,
              interest earned, Element replacement cost, or reduced Element
              useful life.
            </p>

            <h2 className="section" style={{ marginTop: 64 }}>Questions or Requests?</h2>
            <div className="section-rule" />
            <p>
              Contact the Board or the management office for the underlying
              component inventory, the full year-by-year projection, or to
              discuss the assumptions behind any figure in this report.
            </p>
            <p style={{ fontSize: 10.5, color: "var(--slate)", fontStyle: "italic" }}>
              Redistribution outside members, lenders, insurers, prospective
              buyers, and legal counsel is at the Board&rsquo;s discretion.
            </p>
          </div>
          <div className="pf"><div>{footerLine}</div><div>Page {5 + whyOffset} of {totalPages}</div></div>
        </section>

        {/* ═════ Page 5 · Appendix — Elements Inventory ═════
            2026-03-05 (Dave) — Per-element schedule inside a compact
            reference table: display ID, description, current cost,
            useful life when new, remaining life, and next planned
            replacement year (studyYear + remaining_useful_life).
            Filtered to `included_in_plan === true` because members
            should only see components that actually drive the plan.
            Long lists paginate by falling through the page's native
            print pagination — we don't cap the count here, the
            Board's Elements Screen is the source of truth. */}
        <section className="page" id="p5">
          <div className="page-inner">
            <div className="ph">
              <div className="brand"><span className="u">u</span><span className="r"><em>Reserve</em> Planning, LLC</span></div>
              <div className="meta">{metaLine}</div>
            </div>

            <h2 className="section" style={{ marginTop: 2 }}>Appendix · Common Elements</h2>
            <div className="section-rule" />
            <p style={{ marginBottom: 10 }}>
              Every component currently included in the plan, with its
              cost basis, useful life, remaining life, and the year of
              its next scheduled replacement. Values reflect the active
              scenario and match the projection on the preceding pages.
            </p>

            <table className="u appx-elements" data-testid="report-elements-appendix">
              <thead>
                {/* 2026-03-06 (Dave) — Banner row inside <thead>.
                    Because <thead> repeats at the top of every
                    physical page (display: table-header-group),
                    this row carries the brand header on the
                    appendix's overflow pages (physical 7, 8) that
                    would otherwise render bare. On the first
                    physical appendix page, the inline `.ph` above
                    the table already carries the same info — we
                    hide it via `.page#p5 .ph { display: none }` in
                    the print CSS so the two don't stack. Colspan
                    covers all six columns. */}
                <tr className="appx-brand-row" data-testid="report-elements-brand-row">
                  <th colSpan={6} className="appx-brand-cell">
                    <div className="brand"><span className="u">u</span><span className="r"><em>Reserve</em> Planning, LLC</span></div>
                    <div className="meta">{metaLine}</div>
                  </th>
                </tr>
                <tr>
                  <th style={{ width: "8%" }}>ID</th>
                  <th style={{ width: "38%" }}>Description</th>
                  <th style={{ width: "13%", textAlign: "right" }}>Cost</th>
                  <th style={{ width: "13%", textAlign: "right" }}>Useful Life<div style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none", fontSize: 8.5, color: "var(--slate)" }}>(when new)</div></th>
                  <th style={{ width: "13%", textAlign: "right" }}>Remaining Life</th>
                  <th style={{ width: "15%", textAlign: "right" }}>Next Replacement</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // 2026-03-06 (Dave) — Group by category with per-group
                  // sub-totals. Steps:
                  //   1. Build `{el, displayId, cleanDesc, category}` rows
                  //      applying the same legacy-name fallback as
                  //      before for missing `element_display_id`.
                  //   2. Normalize `displayId` to "NNN.NN" (2-decimal
                  //      canonical form). Preserve ≥3-decimal IDs
                  //      verbatim so the Slice-5f "NNN.NNN" convention
                  //      still round-trips.
                  //   3. Bucket by category, sort groups A→Z, sort
                  //      inside each group by numeric displayId.
                  //   4. Emit a header row per group, its items, then a
                  //      subtotal row (Cost column only).
                  const items = (assoc.elements || [])
                    .filter((el) => el.included_in_plan !== false)
                    .map((el) => {
                      const rawDesc = el.description || el.name || "";
                      const nameMatch = String(el.name || "").match(/^(\d+(?:\.\d+)?)\s+(.*)$/);
                      const fallbackId = nameMatch ? nameMatch[1] : "";
                      const rawId = el.element_display_id || fallbackId || "";
                      // NNN.NNN+ (≥3 decimals) preserved verbatim;
                      // everything else parsed and re-emitted as
                      // "NNN.NN" so bare integers surface with the
                      // trailing "00" (e.g., "358" → "358.00").
                      let displayId = rawId || "—";
                      if (rawId) {
                        if (/^\d+\.\d{3,}$/.test(rawId) || /^\d{3}\.\d{2}\.\d{3}$/.test(rawId)) {
                          displayId = rawId;
                        } else {
                          const n = parseFloat(rawId);
                          if (Number.isFinite(n)) displayId = n.toFixed(3);
                        }
                      }
                      const cleanDesc = (!el.element_display_id && nameMatch && rawDesc === el.name)
                        ? nameMatch[2]
                        : rawDesc;
                      const cost = Number(el.extended_cost ?? el.current_cost ?? 0);
                      const category = (el.category || "Uncategorized").trim() || "Uncategorized";
                      return { el, displayId, cleanDesc, category, cost };
                    });

                  const byCategory = new Map();
                  for (const it of items) {
                    if (!byCategory.has(it.category)) byCategory.set(it.category, []);
                    byCategory.get(it.category).push(it);
                  }
                  const groups = Array.from(byCategory.entries())
                    .map(([category, rows]) => ({
                      category,
                      rows: rows.slice().sort((a, b) =>
                        String(a.displayId).localeCompare(String(b.displayId), undefined, { numeric: true })
                      ),
                      subtotal: assoc.element_totals?.by_category?.[category] ?? 0, // server-computed
                    }))
                    .sort((a, b) => a.category.localeCompare(b.category));

                  const out = [];
                  groups.forEach((g) => {
                    out.push(
                      <tr key={`grp-${g.category}`} data-testid={`report-elements-group-${g.category}`}>
                        <td
                          colSpan={6}
                          style={{
                            background: "#F4F7FA",
                            fontWeight: 700,
                            fontSize: 9.5,
                            letterSpacing: ".08em",
                            textTransform: "uppercase",
                            color: "var(--navy)",
                            paddingTop: 6,
                            paddingBottom: 4,
                          }}
                        >
                          {g.category}
                        </td>
                      </tr>
                    );
                    g.rows.forEach(({ el, displayId, cleanDesc, cost }) => {
                      const rem = Number(el.remaining_useful_life ?? 0);
                      const nextYear = studyYear + rem;
                      out.push(
                        <tr key={el.element_id}>
                          <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{displayId}</td>
                          <td>{cleanDesc || "—"}</td>
                          <td style={{ textAlign: "right", fontFamily: "'IBM Plex Mono', monospace" }}>${Math.round(cost).toLocaleString()}</td>
                          <td style={{ textAlign: "right", fontFamily: "'IBM Plex Mono', monospace" }}>{Number(el.useful_life_when_new ?? 0)} yrs</td>
                          <td style={{ textAlign: "right", fontFamily: "'IBM Plex Mono', monospace" }}>{rem} yrs</td>
                          <td style={{ textAlign: "right", fontFamily: "'IBM Plex Mono', monospace" }}>{rem > 0 ? nextYear : studyYear}</td>
                        </tr>
                      );
                    });
                    out.push(
                      <tr key={`sub-${g.category}`} data-testid={`report-elements-subtotal-${g.category}`}>
                        <td />
                        <td style={{ textAlign: "right", fontStyle: "italic", color: "var(--slate)" }}>
                          Subtotal · {g.category}
                        </td>
                        <td style={{ textAlign: "right", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, borderTop: "1px solid var(--hair)" }}>
                          ${Math.round(g.subtotal).toLocaleString()}
                        </td>
                        <td style={{ borderTop: "1px solid var(--hair)" }} />
                        <td style={{ borderTop: "1px solid var(--hair)" }} />
                        <td style={{ borderTop: "1px solid var(--hair)" }} />
                      </tr>
                    );
                  });
                  const grandTotal = assoc.element_totals?.total_all ?? 0; // server-computed
                  const gtCell = { borderTop: "2px solid var(--navy)", paddingTop: 6, color: "var(--navy)", fontWeight: 800 };
                  out.push(
                    <tr key="grand-total" data-testid="report-elements-grand-total" style={{ breakInside: "avoid" }}>
                      <td style={gtCell} />
                      <td style={{ ...gtCell, textAlign: "right", textTransform: "uppercase", letterSpacing: ".06em", fontSize: 9.5 }}>
                        Grand Total · <span data-testid="report-elements-grand-count">{items.length}</span> {items.length === 1 ? "Element" : "Elements"}
                      </td>
                      <td style={{ ...gtCell, textAlign: "right", fontFamily: "'IBM Plex Mono', monospace" }} data-testid="report-elements-grand-amount">
                        ${Math.round(grandTotal).toLocaleString()}
                      </td>
                      <td style={gtCell} />
                      <td style={gtCell} />
                      <td style={gtCell} />
                    </tr>
                  );
                  return out;
                })()}
              </tbody>
            </table>
          </div>
          <div className="pf"><div>{footerLine}</div><div>Page {6 + whyOffset} of {totalPages}</div></div>
        </section>

      </div>
    </div>
  );
}

// Scoped CSS: mirrors the earlier static mockup so the report reads
// identically. Kept as one string so the report file is self-contained
// and can be replaced/re-styled without touching the app shell CSS.
const styles = `
.reserve-budget-report-root{ --navy:#1C4E80; --sky:#88BFDE; --sky-mid:#5379A0; --sky-light:#A5BAD0; --coral:#EA6A47; --ink:#1A1A1A; --slate:#5A6B75; --hair:#E8ECF1; --paper:#FDFDFB; }
.reserve-budget-report-root, .reserve-budget-report-root *{ box-sizing:border-box; }
.reserve-budget-report-root{ background:#EDEEF0; font-family:"Source Sans 3", system-ui, sans-serif; color:var(--ink); height:100vh; height:100dvh; overflow-y:auto; overflow-x:hidden; -webkit-overflow-scrolling:touch; }
.reserve-budget-report-root .banner{ position:sticky; top:0; z-index:50; background:var(--navy); color:#fff; padding:12px 24px; font-size:12px; letter-spacing:.14em; text-transform:uppercase; font-weight:700; display:flex; align-items:center; justify-content:space-between; border-bottom:2px solid var(--coral); }
.reserve-budget-report-root .banner .tag{ background:var(--coral); color:#fff; font-size:10px; padding:2px 8px; border-radius:3px; letter-spacing:.14em; }
.reserve-budget-report-root .banner .dl-pdf-btn{ display:inline-flex; align-items:center; background:#fff; color:var(--navy); border:1px solid #fff; border-radius:3px; font-size:11px; letter-spacing:.08em; font-weight:800; text-transform:uppercase; padding:4px 10px; cursor:pointer; transition:background 120ms ease, color 120ms ease; font-family:inherit; }
.reserve-budget-report-root .banner .dl-pdf-btn:hover{ background:var(--coral); color:#fff; border-color:var(--coral); }
.reserve-budget-report-root .banner .dl-pdf-btn:disabled{ background:#EDEEF0; color:var(--slate); border-color:#EDEEF0; cursor:progress; }
.reserve-budget-report-root .banner .dl-pdf-btn:focus-visible{ outline:2px solid var(--coral); outline-offset:2px; }
.reserve-budget-report-root .pdf-error-toast{ position:fixed; top:56px; right:24px; z-index:60; background:#FEF6F3; border:1px solid var(--coral); border-left:4px solid var(--coral); border-radius:3px; padding:8px 12px; font-size:11.5px; color:var(--ink); box-shadow:0 4px 12px rgba(0,0,0,.12); max-width:360px; }
.reserve-budget-report-root .stack{ padding:20px 0 60px; display:flex; flex-direction:column; align-items:center; gap:20px; }
.reserve-budget-report-root .page{ position:relative; width:8.5in; min-height:11in; background:var(--paper); box-shadow:0 8px 24px rgba(0,0,0,.12), 0 1px 2px rgba(0,0,0,.06); padding:.55in .6in 1.1in; overflow:hidden; }
.reserve-budget-report-root .watermark{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; pointer-events:none; z-index:0; }
.reserve-budget-report-root .watermark span{ transform:rotate(-28deg); font-size:22px; font-weight:900; letter-spacing:.02em; color:rgba(28,78,128,0.045); text-align:center; line-height:1.4; max-width:70%; }
.reserve-budget-report-root .page-inner{ position:relative; z-index:1; padding-bottom:.2in; }
.reserve-budget-report-root .ph{ display:flex; align-items:baseline; justify-content:space-between; border-bottom:2px solid var(--coral); padding-bottom:6px; margin-bottom:14px; }
.reserve-budget-report-root .ph .brand{ display:flex; align-items:baseline; gap:0; font-weight:900; font-size:16px; }
.reserve-budget-report-root .ph .brand .u{ color:var(--navy); }
.reserve-budget-report-root .ph .brand .r{ color:var(--navy); }
.reserve-budget-report-root .ph .brand em{ font-style:italic; font-weight:900; }
.reserve-budget-report-root .ph .meta{ font-family:"IBM Plex Mono", monospace; font-size:10.5px; font-weight:700; color:#3B4650; letter-spacing:.06em; }
.reserve-budget-report-root .pf{ position:absolute; left:.6in; right:.6in; bottom:.6in; display:flex; align-items:center; justify-content:space-between; font-family:"IBM Plex Mono", monospace; font-size:10px; font-weight:700; color:#3B4650; letter-spacing:.08em; border-top:1px solid var(--hair); padding-top:10px; z-index:1; min-height:20px; }
.reserve-budget-report-root h1.doc-title{ font-size:32px; font-weight:900; color:var(--navy); margin:0 0 8px; letter-spacing:-.01em; }
.reserve-budget-report-root h2.section{ font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:var(--navy); font-weight:900; margin:0 0 4px; }
.reserve-budget-report-root .section-rule{ height:2px; background:var(--coral); width:44px; margin-bottom:12px; }
.reserve-budget-report-root h3.h3{ font-size:14px; color:var(--navy); font-weight:700; margin:14px 0 6px; }
.reserve-budget-report-root p{ font-size:11.5px; line-height:1.6; margin:0 0 10px; color:var(--ink); }
.reserve-budget-report-root p.lede{ font-size:13px; line-height:1.6; }
.reserve-budget-report-root .badge{ display:inline-block; padding:2px 8px; border:1px solid var(--navy); border-radius:3px; font-size:10px; letter-spacing:.08em; color:var(--navy); font-weight:700; text-transform:uppercase; background:#fff; }
.reserve-budget-report-root .callout{ border-left:3px solid var(--coral); background:#FEF6F3; padding:10px 14px; margin:12px 0; font-size:11.5px; line-height:1.55; }
.reserve-budget-report-root .callout .label{ font-size:9.5px; letter-spacing:.14em; color:var(--coral); font-weight:900; text-transform:uppercase; margin-bottom:4px; }
.reserve-budget-report-root .callout.navy{ border-color:var(--navy); background:#F0F5FA; }
.reserve-budget-report-root .callout.navy .label{ color:var(--navy); }
.reserve-budget-report-root .kpi-strip{ display:grid; grid-template-columns:repeat(7, 1fr); gap:5px; margin:16px 0 4px; }
.reserve-budget-report-root .kpi{ border:1px solid var(--hair); border-radius:3px; padding:7px 8px; background:#fff; }
.reserve-budget-report-root .kpi .h{ font-size:8px; letter-spacing:.10em; text-transform:uppercase; color:var(--navy); font-weight:700; margin-bottom:3px; line-height:1.15; min-height:22px; }
.reserve-budget-report-root .kpi .v{ font-family:"IBM Plex Mono", monospace; font-size:14px; font-weight:700; color:var(--navy); line-height:1.15; }
.reserve-budget-report-root .kpi .v.coral{ color:var(--coral); }
.reserve-budget-report-root .kpi .sub{ font-size:8.5px; color:var(--slate); margin-top:2px; }
.reserve-budget-report-root table.u{ width:100%; border-collapse:collapse; font-size:11.5px; }
.reserve-budget-report-root table.u th, .reserve-budget-report-root table.u td{ padding:7px 10px; border-bottom:1px solid var(--hair); text-align:left; vertical-align:top; }
.reserve-budget-report-root table.u th{ font-size:9.5px; letter-spacing:.12em; text-transform:uppercase; color:var(--navy); font-weight:700; background:#F5F8FB; }
/* 2026-03-06 (Dave) — Banner row rendered inside the appendix
   table's <thead>. Repeats on every physical page (Chromium
   table-header-group behaviour) so the brand strip + meta line
   appear at the top of overflow pages (physical 7, 8). Hidden on
   screen to avoid stacking with the inline .ph in the section body
   during on-screen preview; shown in print via @media print below. */
.reserve-budget-report-root table.appx-elements tr.appx-brand-row { display: none; }
.reserve-budget-report-root table.appx-elements th.appx-brand-cell {
  background: transparent !important;
  border-bottom: 2px solid var(--coral) !important;
  padding: 0 0 6px 0 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}
.reserve-budget-report-root table.appx-elements th.appx-brand-cell .brand { display:flex; align-items:baseline; gap:0; font-weight:900; font-size:16px; color:var(--navy); }
.reserve-budget-report-root table.appx-elements th.appx-brand-cell .brand em { font-style:italic; font-weight:900; }
.reserve-budget-report-root table.appx-elements th.appx-brand-cell .meta { font-family:"IBM Plex Mono", monospace; font-size:10.5px; font-weight:700; color:#3B4650; letter-spacing:.06em; margin-left:auto; }
.reserve-budget-report-root table.appx-elements th.appx-brand-cell > * { display: inline-block; }
@media print {
  .reserve-budget-report-root table.appx-elements tr.appx-brand-row { display: table-row; }
  .reserve-budget-report-root table.appx-elements th.appx-brand-cell { display: table-cell; }
  /* Lay out brand + meta on one row inside the cell. */
  .reserve-budget-report-root table.appx-elements th.appx-brand-cell {
    padding: 6px 0 6px 0 !important;
  }
  .reserve-budget-report-root table.appx-elements th.appx-brand-cell::after {
    content: "";
    display: table;
    clear: both;
  }
  .reserve-budget-report-root table.appx-elements th.appx-brand-cell .brand { float: left; }
  .reserve-budget-report-root table.appx-elements th.appx-brand-cell .meta { float: right; }
}
.reserve-budget-report-root table.u tr:last-child td{ border-bottom:none; }
.reserve-budget-report-root table.fl{ width:100%; border-collapse:collapse; font-size:11px; margin-top:4px; }
.reserve-budget-report-root table.fl th, .reserve-budget-report-root table.fl td{ padding:6px 8px; border-bottom:1px solid var(--hair); text-align:center; }
.reserve-budget-report-root table.fl th{ font-size:9px; letter-spacing:.12em; text-transform:uppercase; color:var(--navy); font-weight:700; background:#F5F8FB; }
.reserve-budget-report-root table.fl td.pct{ font-weight:700; color:var(--navy); }
.reserve-budget-report-root table.fl td.pct.band-mod{ color:#B78900; }
.reserve-budget-report-root table.fl td.pct.band-low{ color:#0F7A55; }
.reserve-budget-report-root table.fl td.pct.band-high{ color:#EA6A47; }
/* 2026-03-06 (Dave) — Risk-band row shading applied to the Funding
   Level % and Risk Band cells only (not the whole row) so the
   colour cue reads as a "chip" attached to the two related columns.
   Palette: pale green / pale amber / pale red — light enough to
   keep the numeric text (Plex-family colours above) legible. */
.reserve-budget-report-root table.fl td.band-low{ background:#E4F5EA; }
.reserve-budget-report-root table.fl td.band-mod{ background:#FCF1D3; }
.reserve-budget-report-root table.fl td.band-high{ background:#FBDDD2; }
.reserve-budget-report-root table.fl td.yr{ font-family:"IBM Plex Mono", monospace; color:var(--slate); }
.reserve-budget-report-root .waterfall-wrap{ border:1px solid var(--hair); border-radius:3px; padding:10px 12px 8px; background:#fff; }
.reserve-budget-report-root .assumptions-strip{ display:flex; flex-wrap:wrap; align-items:baseline; gap:6px 10px; padding:5px 9px; margin:0 0 12px; background:#F5F8FB; border:1px solid var(--hair); border-left:3px solid var(--sky); border-radius:3px; font-size:10.5px; color:var(--ink); }
.reserve-budget-report-root .assumptions-strip .a-label{ font-size:9px; letter-spacing:.14em; text-transform:uppercase; color:var(--navy); font-weight:900; margin-right:2px; }
.reserve-budget-report-root .assumptions-strip .a-item{ display:inline-flex; align-items:baseline; gap:4px; }
.reserve-budget-report-root .assumptions-strip .a-k{ color:var(--slate); }
.reserve-budget-report-root .assumptions-strip .a-k-dark{ color:#3F4B57; }
.reserve-budget-report-root .assumptions-strip .a-v{ font-family:"IBM Plex Mono", monospace; font-weight:700; color:var(--navy); }
.reserve-budget-report-root .assumptions-strip .a-sep{ color:var(--hair); }
.reserve-budget-report-root .contrib-grid{ display:grid; grid-auto-flow:column; grid-auto-columns:1fr; gap:0; }
.reserve-budget-report-root .contrib-grid .a-sep{ display:none; }
.reserve-budget-report-root .contrib-grid .a-item{ flex-direction:column; align-items:center; gap:1px; padding:0 4px; border-left:1px solid var(--hair); }
.reserve-budget-report-root .contrib-grid .a-item:first-child{ border-left:none; }
.reserve-budget-report-root .contrib-grid .a-k{ font-size:9.5px; white-space:nowrap; }.reserve-budget-report-root .waterfall-wrap .cap{ font-size:9.5px; color:var(--slate); letter-spacing:.06em; margin-top:2px; text-align:center; }
.reserve-budget-report-root .mix-strip{ display:flex; align-items:flex-start; gap:14px; margin-top:8px; padding-top:8px; border-top:1px solid var(--hair); }
.reserve-budget-report-root .mix-strip svg{ flex-shrink:0; }
.reserve-budget-report-root .mix-legend{ flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; padding-top:4px; }
.reserve-budget-report-root .mix-legend .mix-title{ font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:var(--navy); font-weight:900; margin-bottom:4px; }
.reserve-budget-report-root .mix-legend .mix-row{ display:grid; grid-template-columns: 10px 1fr 42px 56px; gap:6px; align-items:center; font-size:10.5px; }
.reserve-budget-report-root .mix-legend .mix-row .dot{ width:8px; height:8px; border-radius:2px; }
.reserve-budget-report-root .mix-legend .mix-row .lbl{ color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.reserve-budget-report-root .mix-legend .mix-row .pct{ font-family:"IBM Plex Mono", monospace; color:var(--slate); text-align:right; }
.reserve-budget-report-root .mix-legend .mix-row .amt{ font-family:"IBM Plex Mono", monospace; color:var(--navy); font-weight:700; text-align:right; }
@media print{ .reserve-budget-report-root{ background:#fff; } .reserve-budget-report-root .banner, .reserve-budget-report-root .stack{ padding:0; } .reserve-budget-report-root .banner{ display:none; } .reserve-budget-report-root .page{ box-shadow:none; margin:0; page-break-after:always; } .reserve-budget-report-root .page:last-child{ page-break-after:auto; } }
@page{ size: 8.5in 11in; margin: 0; }
@media (max-width:900px){ .reserve-budget-report-root .stack{ padding:12px 0 40px; gap:12px; } .reserve-budget-report-root .page{ zoom:0.72; } }
@media (max-width:520px){ .reserve-budget-report-root .page{ zoom:0.44; } }
`;
